import axios from 'axios';
import { IDatabase } from '../../../database';
import { FlowyFoundation, FlowyGlobal } from '../../../flowy';
import { RootGizmo } from '../../../gizmos';
import {
  IFilledFormV2,
  IGizmoEvent,
  IRealmFilledForm,
  IUserV2,
} from '../../../types';
import { gnov, snov } from '../../../utils';
import { rootGizmoDataV2ToV3 } from '../../../gizmos/utils';
import uploadFiles from './upload-files';
import uploadUserEvents from './upload-user-events';
import formAuthSignIn from './form-auth-sign-in';
import uploadLocations from './upload-locations';
import downloadConfigurations from './download-configurations';

interface IParams {
  publicRG?: boolean;
  useFileApiV2?: boolean;
  context: {
    apiUrl: string;
    user?: IUserV2;
    database?: IDatabase;
    global?: FlowyGlobal;
    foundation?: FlowyFoundation;
  };
}

/**
 * Synchronize with the server, right now it only uploads root gizmo data
 *
 * @param {boolean} publicRG public root gizmo, it means it's an anon user
 * @returns
 */
function sync({ publicRG, useFileApiV2, context }: IParams): Promise<void> {
  return new Promise<void>(async resolve => {
    const { apiUrl, user, database, global, foundation } = context;

    let offline = false;
    if (global?.getConfig()) {
      offline = gnov(global.getConfig(), 'offline');
    }

    // This variable will be set to false when we're doing authentication
    // TODO: should it also be set to false on public root gizmo?
    let updateSync = true;
    let url: string | undefined;
    let headers = {};

    let rootGizmo: RootGizmo | undefined = foundation?.getRootGizmo();
    if (rootGizmo?.getConfig()?.type !== 'form') {
      const nestedForms = foundation
        ?.getRootGizmo()
        ?.findGizmos({ type: 'form' });
      if (nestedForms && Array.isArray(nestedForms) && nestedForms.length > 0) {
        rootGizmo = nestedForms[0] as RootGizmo;
      }
    }

    if (user?.credentials) {
      const credentials: any = user.credentials;
      url = `${apiUrl}/main_api/v1/root_gizmo/data/`;
      if (publicRG) {
        url = `${apiUrl}/main_api/v1/public/root_gizmo/data/`;
      }

      headers = {
        ...credentials,
      };
    } else if (publicRG) {
      url = `${apiUrl}/main_api/v1/public/root_gizmo/data/`;
    }

    let registers: IFilledFormV2[] = [];
    let savedFilledForms: IRealmFilledForm[] = [];
    if (database) {
      const params: any = {};
      if (database.getType() !== 'memory') {
        // When we have database that's not memory we're going to upload only the registers
        // that have been updated.
        params['updated'] = true;
      }
      // Right now we're going to bring all the registers saved in the database
      savedFilledForms = await database.search({
        type: 'filledForms',
        params,
      });

      // We're going to tranform to only the registers that are not savedUploaded
      registers = savedFilledForms
        .filter(r => r.offlineStatus !== 'savedUploaded')
        .map(r => r.gizmoData);

      const findAddedPassword = registers.find(
        r => gnov(r, 'answers.added_password') !== undefined
      );
      // If the formId is 'login-form-fid' it means it's a login form
      const findLoginRegister = registers.find(
        r => gnov(r, 'formId') === 'login-form-fid'
      );

      if (findLoginRegister) {
        let rootGizmoConfig = foundation?.getRootGizmo()?.getConfig();

        // If the root gizmo is not a form, we're going to look for a nested form
        if (rootGizmoConfig?.type !== 'form') {
          const nestedForms = foundation
            ?.getRootGizmo()
            ?.findGizmos({ type: 'form' });

          if (
            nestedForms &&
            Array.isArray(nestedForms) &&
            nestedForms.length > 0
          ) {
            // And assign the config of the first nested form
            rootGizmoConfig = nestedForms[0].getConfig();
          }
        }

        if (rootGizmoConfig?.ops?.form?.auth?.rootGizmoFid) {
          const formId = rootGizmoConfig.ops.form.auth.rootGizmoFid;
          url = `${apiUrl}/main_api/v1/form_auth/sign_in/${formId}`;
          updateSync = false;
          offline = false;
        }
      } else if (findAddedPassword) {
        // This means it's a sign up form
        url = `${apiUrl}/main_api/v1/form_auth/sign_up/${findAddedPassword.formId}`;
        useFileApiV2 = true;
        updateSync = false;
        offline = false;
      }

      if (updateSync && !offline) {
        // We're going to calculate all the sync object
        await global?.updateSyncObject({
          type: 'start',
        });
      }

      if (!offline) {
        await uploadUserEvents({
          context: {
            apiUrl,
            user,
            database,
            global,
          },
        });
        await uploadLocations({
          context: {
            apiUrl,
            user,
            database,
            global,
          },
        });
      }
    }

    let allRegistersUploaded: boolean | undefined = undefined;
    // We're default to true because maybe the user doesn't have any file to upload
    let allFilesUploaded: boolean | undefined = undefined;

    let uploadedAttachmentIds: string[] = [];
    let uploadedAttachments: {
      attachmentId: string;
      extension: string;
    }[] = [];
    if (url && registers.length > 0 && database && !offline) {
      const {
        status,
        uploadedAttachmentIds: uai,
        uploadedAttachments: ua,
      } = await uploadFiles({
        registers,
        useFileApiV2,
        context: {
          database,
          apiUrl,
          foundation,
          user,
        },
      });

      if (status === 'all_files_uploaded') {
        allFilesUploaded = true;
        uploadedAttachmentIds = uai;
        uploadedAttachments = ua;
      } else if (status === 'error_uploading_files') {
        allFilesUploaded = false;
      }
    }

    if (
      url &&
      registers.length > 0 &&
      database &&
      (allFilesUploaded === true || allFilesUploaded === undefined) &&
      !offline
    ) {
      // We're getting the platform interface in order to get the stored files
      const platformInterface = foundation?.getPlatformInterface();
      // TODO: add syncProgress

      allRegistersUploaded = false;

      try {
        const savedRootGizmoDataResponse = await axios.post(
          url,
          { registers },
          { headers }
        );

        if (savedRootGizmoDataResponse.status === 200) {
          if (updateSync) {
            const sync = global?.getSync();

            if (sync && global) {
              const total = gnov(sync, 'progressBar.total');
              const progress = gnov(sync, 'progressBar.progress');

              if (progress + registers.length <= total) {
                snov(sync, 'progressBar.progress', progress + registers.length);
                global.setSync(sync);
              }
            }
          }

          if (url.includes('/sign_in/')) {
            const { data } = savedRootGizmoDataResponse;
            const user: IUserV2 = {
              username: savedRootGizmoDataResponse.headers.uid,
              id: 'form-auth-user',
              credentials: {
                'access-token':
                  savedRootGizmoDataResponse.headers['access-token'],
                client: savedRootGizmoDataResponse.headers.client,
                'token-type': savedRootGizmoDataResponse.headers['token-type'],
                uid: savedRootGizmoDataResponse.headers.uid,
                expiry: savedRootGizmoDataResponse.headers.expiry,
                'auth-form-fid':
                  savedRootGizmoDataResponse.headers['auth-form-fid'],
              },
              // TODO: add the user data
              // variables: {}
            };

            if (data.data) {
              user.id = data.fid;
              // We're creating this in order to be able to use the function to transform
              const dummyFilledform: IFilledFormV2 = {
                id: '63f3037295b17d2ea608347d',
                formId: '63e02a328e1e8300b2bb200e',
                formVersion: 8,
                fid:
                  '63e02a328e1e8300b2bb200e_63e028098e1e8300b2bb200c_1676869259',
                filledFormId:
                  '63e02a328e1e8300b2bb200e_63e028098e1e8300b2bb200c_1676869259',
                answers: data.data,
                source: 'mobile',
                version: 1,
                username: 'axe',
                epoch_start: 1676869259,
                epoch_end: 1676870508,
                status: 's1',
              };
              const v3FilledForm = rootGizmoDataV2ToV3({
                data: dummyFilledform,
              });
              // TODO: transform from v2 to v3
              user.variables = v3FilledForm.d;
            }
            if (data.gizmos) {
              // TODO: tranform from v2 to v3
              user.gizmos = data.gizmos;
            }

            await platformInterface?.setUser(user);
            database?.setUser(user);

            await foundation
              ?.getGlobal()
              ?.getAnalytics()
              ?.createUserEvent({
                category: 'session',
                type: 'loggedIn',
              });
            await foundation
              ?.getGlobal()
              ?.getAnalytics()
              ?.uploadUserEvents();

            await foundation?.getGlobal()?.sendNotification({
              notification: {
                fid: 'n0',
                type: 'success',
                label: 'Ingreso exitoso',
              },
            });
            await foundation?.getPlatformCallbacks()?.platformOperationCB({
              operation: 'navigation.link',
              params: {
                path: '/',
              },
            });
          } else {
            allRegistersUploaded = true;
            // await foundation?.getGlobal()?.sendNotification({
            //   notification: {
            //     fid: 'n0',
            //     type: 'success',
            //     label: 'Formato almacenado',
            //   },
            // });
            // await foundation?.getPlatformCallbacks()?.platformOperationCB({
            //   operation: 'navigation.back',
            //   params: {},
            // });
          }

          for (const savedFilledForm of savedFilledForms) {
            // When sucessfully uploaded we're going to update the offlineStatus
            const gizmoData = savedFilledForm.gizmoData;
            gizmoData.uploaded = true;
            gizmoData.draft = false;
            gizmoData.updated = false;
            gizmoData.offlineStatus = 'savedUploaded';

            await database.set({
              type: 'filledForms',
              value: gizmoData,
              update: true,
            });

            // After we've sent the data to the server we're going to delete it from the database
            // await database.delete({
            //   type: 'filledForms',
            //   fid: savedFilledForm.fid,
            // });
          }
        }
      } catch (e) {
        console.log('=============e: ', e);
        console.log('j: ', JSON.stringify(e));
        console.log('code: ', e.code);
        if (e.response) {
          const { data } = e.response;
          let errorMessage = 'Error';

          if (data && data.detail === 'Incorrect username or password') {
            errorMessage = 'Usuario o contraseña incorrectos';
          } else if (data && data.detail === 'User already exists') {
            errorMessage = 'El usuario ya existe';
          }

          // 401 means that the user is not authenticated (user or password incorrect)
          // 409 means that the user already exists
          if (e.response.status === 401 || e.response.status === 409) {
            const event: IGizmoEvent = {
              type: 'root-gizmo.operation',
              src: {
                type: 'flowy-foundation',
              },
              data: {
                rootGizmoOperation: {
                  type: 'form.clear-processing',
                },
              },
            };

            let rootGizmo = foundation?.getRootGizmo();
            if (rootGizmo?.getConfig()?.type !== 'form') {
              const nestedForms = foundation
                ?.getRootGizmo()
                ?.findGizmos({ type: 'form' });
              if (
                nestedForms &&
                Array.isArray(nestedForms) &&
                nestedForms.length > 0
              ) {
                rootGizmo = nestedForms[0] as RootGizmo;
              }
            }
            rootGizmo?.sendEvent(event);

            foundation?.getGlobal()?.sendNotification({
              notification: {
                fid: 'incorrect-username-or-password',
                type: 'error',
                label: errorMessage,
              },
            });

            // If we have a 401 error and we're trying to sign-in we're going to remove the
            // register from the database.
            if (url.includes('/sign_in/')) {
              for (const savedFilledForm of savedFilledForms) {
                // After we've sent the data to the server we're going to delete it from the database
                await database.delete({
                  type: 'filledForms',
                  fid: savedFilledForm.fid,
                });
              }
            }
          } else {
            const event: IGizmoEvent = {
              type: 'root-gizmo.operation',
              src: {
                type: 'flowy-foundation',
              },
              data: {
                rootGizmoOperation: {
                  type: 'form.clear-processing',
                },
              },
            };

            let rootGizmo = foundation?.getRootGizmo();
            if (rootGizmo?.getConfig()?.type !== 'form') {
              const nestedForms = foundation
                ?.getRootGizmo()
                ?.findGizmos({ type: 'form' });
              if (
                nestedForms &&
                Array.isArray(nestedForms) &&
                nestedForms.length > 0
              ) {
                rootGizmo = nestedForms[0] as RootGizmo;
              }
            }
            rootGizmo?.sendEvent(event);

            if (database.getType() === 'memory') {
              foundation?.getGlobal()?.sendNotification({
                notification: {
                  fid: 'incorrect-username-or-password',
                  type: 'error',
                  label:
                    'Error al subir el formato, favor de intentar de nuevo.',
                },
              });
            }
          }
        } else {
          const event: IGizmoEvent = {
            type: 'root-gizmo.operation',
            src: {
              type: 'flowy-foundation',
            },
            data: {
              rootGizmoOperation: {
                type: 'form.clear-processing',
              },
            },
          };

          let rootGizmo = foundation?.getRootGizmo();
          if (rootGizmo?.getConfig()?.type !== 'form') {
            const nestedForms = foundation
              ?.getRootGizmo()
              ?.findGizmos({ type: 'form' });
            if (
              nestedForms &&
              Array.isArray(nestedForms) &&
              nestedForms.length > 0
            ) {
              rootGizmo = nestedForms[0] as RootGizmo;
            }
          }
          rootGizmo?.sendEvent(event);

          foundation?.getGlobal()?.sendNotification({
            notification: {
              fid: 'incorrect-username-or-password',
              type: 'error',
              label: 'Error al subir el formato, favor de intentar de nuevo.',
            },
          });
        }
      }
    }

    if (allFilesUploaded === false) {
      const event: IGizmoEvent = {
        type: 'root-gizmo.operation',
        src: {
          type: 'flowy-foundation',
        },
        data: {
          rootGizmoOperation: {
            type: 'form.clear-processing',
          },
        },
      };

      let rootGizmo = foundation?.getRootGizmo();
      if (rootGizmo?.getConfig()?.type !== 'form') {
        const nestedForms = foundation
          ?.getRootGizmo()
          ?.findGizmos({ type: 'form' });
        if (
          nestedForms &&
          Array.isArray(nestedForms) &&
          nestedForms.length > 0
        ) {
          rootGizmo = nestedForms[0] as RootGizmo;
        }
      }
      rootGizmo?.sendEvent(event);

      foundation?.getGlobal()?.sendNotification({
        notification: {
          fid: 'incorrect-username-or-password',
          type: 'error',
          label: 'Error al subir archivo, favor de intentar de nuevo.',
        },
      });
    }

    // We were having a situation in which both of the functions were being called twice, so we
    // introduced this variable. So even though the variable could be set two times the functions
    // will only be called once.
    let showNotificationAndGoBack = false;

    if (
      (allRegistersUploaded === true && allFilesUploaded === true) ||
      (allRegistersUploaded === undefined && allFilesUploaded === true) ||
      (allRegistersUploaded === true && allFilesUploaded === undefined)
    ) {
      try {
        // After everything went fine, we're going to clean the saved registers
        const savedFilledForms = (await database?.search({
          type: 'filledForms',
          params: {
            offlineStatus: 'savedUploaded',
          },
        })) as IRealmFilledForm[];

        for (const savedFilledForm of savedFilledForms) {
          await database?.delete({
            type: 'filledForms',
            fid: savedFilledForm.fid,
          });
        }

        // TODO: remove this commented code when we've tested everything is stable
        // if (allFilesUploaded === true && uploadedAttachmentIds.length > 0) {
        if (allFilesUploaded === true && uploadedAttachments.length > 0) {
          // If all files have been uploaded we remove them
          const platformInterface = foundation?.getPlatformInterface();

          // for (const attachmentId of uploadedAttachmentIds) {
          //   await platformInterface?.fileRemove({
          //     fileFid: attachmentId,
          //     path: '',
          //   });
          // }
          for (const attachment of uploadedAttachments) {
            await platformInterface?.fileRemove({
              fileFid: attachment.attachmentId,
              fileExtension: attachment.extension,
              path: '',
            });
          }
        }

        if (url?.includes('main_api/v1/form_auth/sign_up') && rootGizmo) {
          await formAuthSignIn({
            rootGizmo,
            context: {
              apiUrl,
              user,
              foundation,
            },
          });
        }
      } catch (e) {
        console.log('*******3.e: ', e);
      }

      showNotificationAndGoBack = true;

      // TODO: remove after validatin it's stable
      // await foundation?.getGlobal()?.sendNotification({
      //   notification: {
      //     fid: 'n0',
      //     type: 'success',
      //     label: 'Formato almacenado',
      //   },
      // });
      // await foundation?.getPlatformCallbacks()?.platformOperationCB({
      //   operation: 'navigation.back',
      //   action: 'save',
      //   params: {},
      // });
    }

    // If we're working offline we're going to send the notification and go back
    if (offline || database?.getType() !== 'memory') {
      showNotificationAndGoBack = true;
      // TODO: remove after validatin it's stable
      // await foundation?.getGlobal()?.sendNotification({
      //   notification: {
      //     fid: 'n0',
      //     type: 'success',
      //     label: 'Formato almacenado',
      //   },
      // });
      // await foundation?.getPlatformCallbacks()?.platformOperationCB({
      //   operation: 'navigation.back',
      //   params: {},
      // });
    }

    if (showNotificationAndGoBack) {
      await foundation?.getGlobal()?.sendNotification({
        notification: {
          fid: 'n0',
          type: 'success',
          label: 'Formato almacenado',
        },
      });

      if (database?.getType() === 'memory') {
        // We're doing this only with memory database, because it's not memory database this
        // instruction is being sent on the FlowyFoundation submitRootGizmoDataCB function.
        // It's done like this because when it's not memory database we won't block until
        // the sync has finished.
        await foundation?.getPlatformCallbacks()?.platformOperationCB({
          operation: 'navigation.back',
          action: 'save',
          params: {},
        });
      }
    }

    if (database) {
      // TODO: remove this when we've tested everything is stable
      // await downloadConfigurations({
      //   context: {
      //     apiUrl,
      //     user,
      //     database,
      //   },
      // });
      await global?.updateSyncObject({
        type: 'finish',
      });
    }

    resolve();
  });
}

export default sync;
